import {
  Button,
  Dialog,
  Link,
  stylize,
  TriCheckbox,
} from "@envestnet/envreact-component-library";
import classnames from "classnames";
import dompurify from "dompurify";
import React, { useEffect, useMemo, useState } from "react";
import withIntl from "utils/withIntl";
import messages from "./messages";
import styles, { Styles } from "./styles";

interface InjectedProps extends Styles {
  /** Internal - injected from withIntl */
  formatMessage: FormatMessage;

  /** Internal - injected from WithStyles */
  innerRef?: React.RefObject<HTMLDivElement>;
}

interface DefaultProps {
  updatedTnC: string | null;
  acceptUpdatedTnC: Function;
  hasUpdatedTnCAccepted: boolean;
  tncForCidpUser: string | null;
}

export interface DashboardFooterProps extends DefaultProps {
  /** CSS class name to be applied to top level component */
  className?: string;

  /** Inline style object to be applied to top level component */
  style?: React.CSSProperties;

  isDirectLoginEnabled: boolean;
}

const defaultProps: DefaultProps = {
  updatedTnC: null,
  acceptUpdatedTnC: () => {},
  hasUpdatedTnCAccepted: false,
  tncForCidpUser: null,
};

type Props = InjectedProps & DashboardFooterProps;

const DashboardFooter = (props: Props) => {
  const {
    classes,
    style,
    className,
    formatMessage,
    innerRef,

    updatedTnC,
    acceptUpdatedTnC,
    hasUpdatedTnCAccepted,
    tncForCidpUser,
    isDirectLoginEnabled,
  } = props;
  const [openTermsAndCondition, setOpenTermsAndCondition] = useState(false);
  const [showUpdatedTnC, setShowUpdatedTnC] = useState(false);
  // const [showUpdatedTnCReminder, setReminderTnC] = useState(
  //   !hasUpdatedTnCAccepted
  // );

  // TODO: Delete the next line and uncomment the previous 3 lines to reimplement the reminder dialog
  const [showUpdatedTnCReminder, setReminderTnC] = useState(false);
  const [bottom, setBottom] = useState(false);
  const [isLoading, setLoading] = useState(false);

  useEffect(() => {
    if (hasUpdatedTnCAccepted) {
      setReminderTnC(false);
      setLoading(false);
      setShowUpdatedTnC(false);
    }
  }, [hasUpdatedTnCAccepted]);

  useEffect(() => {
    if (showUpdatedTnC) {
      setTimeout(() => {
        const doc: any = document.getElementById("dialog-body");
        if (doc && doc.scrollHeight < 680) {
          setBottom(true);
        }
      }, 20);
    }
  }, [showUpdatedTnC]);

  const year = useMemo(() => new Date().getFullYear(), []);

  const handleScroll = (e) => {
    const dynamicHeight = e.target.scrollHeight - e.target.scrollTop;
    const isBottom = dynamicHeight - e.target.clientHeight < 10;
    setBottom(isBottom);
  };

  const rawHTML = dompurify.sanitize((tncForCidpUser || updatedTnC) ?? "", {
    FORCE_BODY: true,
  });

  const renderAsHtml = () => (
    // eslint-disable-next-line react/no-danger
    <div dangerouslySetInnerHTML={{ __html: rawHTML }} data-testid="TNC"></div> // NOTE: uploaded File is sanitized
  );

  return (
    <div
      ref={innerRef}
      className={classnames(className, classes.container)}
      style={style}
    >
      <div className={classes.footerDiv}>
        <span className={classnames(classes.links, "header-font-family")}>
          <span>©</span>
          {formatMessage(messages.companyName, { year })}
        </span>

        {isDirectLoginEnabled && (
          <Button
            buttonStyle="link"
            className={classnames(classes.links, "header-font-family")}
            name={formatMessage(messages.tnc)}
            onClick={() => {
              setOpenTermsAndCondition(true);
            }}
            style={{ display: tncForCidpUser ? "block" : "none" }}
          />
        )}
        <Link
          href="https://www.envestnet.com/privacy"
          target="_blank"
          className={classnames(classes.links, "header-font-family")}
        >
          {formatMessage(messages.privacyPolicy)}
        </Link>
      </div>
      <Dialog
        open={openTermsAndCondition}
        size="l"
        buttons={[
          {
            buttonType: "spacer",
          },
          {
            buttonType: "primary",
            id: "save",
            title: formatMessage(messages.closeButtonLabel),
          },
        ]}
        closeOnOutsideClick
        onAction={() => setOpenTermsAndCondition(false)}
        title={formatMessage(messages.tnc)}
        titleClass={classes.termsTitleClass}
        className="header-font-family no-border-radius"
        style={{ borderRadius: 0 }}
      >
        <div
          className={classnames(classes.termsStyle, "header-text-font-family")}
        >
          {renderAsHtml()}
        </div>
      </Dialog>
      <Dialog
        open={showUpdatedTnCReminder}
        size="m"
        closeOnOutsideClick={false}
        onAction={() => {}}
        title={formatMessage(messages.updatedTnCTitle)}
        hideCloseButton
        titleClass={classnames(classes.termsTitleClass)}
        style={{ borderRadius: 0 }}
      >
        <div
          className={classnames(classes.termsStyle, "header-text-font-family")}
        >
          {formatMessage(messages.updatedTnCDesc)}
          <div
            className={classes.acceptUpdatedTnCCheckbox}
            data-testid="accept-updated-tnc"
          >
            <TriCheckbox
              className={classes.termsCheckboxStyle}
              onClick={() => setShowUpdatedTnC(true)}
              value={1}
              checked="unchecked"
            />
            <div className={classes.acceptUpdatedTnCCheckboxLabel}>
              <span>{formatMessage(messages.updatedTnCCheckboxTitle)}</span>
              <Button
                buttonStyle="link"
                name={formatMessage(messages.updatedBtnLinkLabel)}
                className={classnames(classes.linkButtonStyle)}
                onClick={() => setShowUpdatedTnC(true)}
              />
              <span>{formatMessage(messages.updatedTnCCheckBoxLabel)}</span>
            </div>
          </div>
        </div>
      </Dialog>
      <Dialog
        open={showUpdatedTnC}
        size="l"
        buttons={[
          {
            buttonType: "spacer",
          },
          {
            buttonType: "secondary",
            id: "0",
            title: formatMessage(messages.cancelBtnLabel),
          },
          {
            buttonType: "primary",
            id: "1",
            title: formatMessage(messages.agreeBtnLabel),
            disabled: !bottom,
            loading: isLoading,
          },
        ]}
        closeOnOutsideClick
        onAction={(id) => {
          if (id === "1") {
            setLoading(true);
            acceptUpdatedTnC();
          } else {
            setReminderTnC(true);
            setShowUpdatedTnC(false);
          }
        }}
        title={formatMessage(messages.tnc)}
        titleClass={classes.termsTitleClass}
        style={{ borderRadius: 0 }}
      >
        <div
          id="dialog-body"
          onScroll={handleScroll}
          className={classnames(
            classes.termsStyle,
            classes.TnCContainer,
            "header-text-font-family"
          )}
        >
          {renderAsHtml()}
        </div>
      </Dialog>
    </div>
  );
};

DashboardFooter.defaultProps = defaultProps;

export default withIntl(stylize<Props, DefaultProps>(styles)(DashboardFooter));
